import { FC, useContext, useEffect, useMemo, useState } from 'react';

import {
  Button,
  Input,
  ITabOption,
  NumberInput,
  Tabs,
  SearchIcon,
  Separator,
  H2,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import {
  CalculatorCash,
  ClientSearch,
  DiscountsField,
  GOODS,
  PromoCodeField,
  ShopGoodsTable,
  ShoppingCart,
  ShopServicesTable,
} from 'components';
import { ShopCombosTable, COMBOS } from 'components/ShopCombosTable';
import { useIsFeatureAvailable } from 'features';
import {
  useEntitySearch,
  usePayment,
  useShoppingCart,
  ISendingPaymentItem,
} from 'hooks';
import { LocalizationContext } from 'localization';
import { DepositModal } from 'modals';
import notification from 'services/notificationService';
import { SettingsContext } from 'settings';
import { EShopItem, EPaymentMethod } from 'types';
import {
  getCartItems,
  getPaymentMethod,
  getShoppingCartTotalPrice,
} from 'utils';

import { IAvailableItems } from '../../PromoCodeManager/interfaces';
import { IFormFields } from '../types';
import { DEFAULT_FORM_VALUES } from './constants';
import s from './ShopForm.module.scss';

export const ShopForm: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const isComboAvailable = useIsFeatureAvailable('combos');

  const TABS: Array<ITabOption<EShopItem>> = [
    { value: EShopItem.GOOD, title: t('category.goods') },
    { value: EShopItem.SERVICE, title: t('category.services') },
    {
      value: EShopItem.COMBO,
      title: t('nav.combos'),
      disabled: !isComboAvailable,
    },
  ];

  const PAYMENT_METHODS = getPaymentMethod(t, true);

  const [tab, setTab] = useState<EShopItem>(EShopItem.GOOD);
  const [isRoleClient, setIsRoleClient] = useState<string | undefined>('');
  const [availableItems, setAvailableItems] = useState<IAvailableItems[]>([]);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid, isSubmitting },
  } = useForm<IFormFields>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  const method = watch('method');
  const discount = watch('discount');
  const promoCode = watch('promoCode');
  const clientUUID = watch('clientUUID');

  const activeDiscount = useMemo(() => {
    if (promoCode?.value.type === 'DISCOUNT') {
      return promoCode.value.value;
    }
    if (discount?.value) {
      return discount.value;
    }

    return null;
  }, [discount, promoCode]);

  const {
    combo,
    setCombo,
    items,
    addItem,
    setItemAmount,
    clearShoppingCart,
    removeItem,
  } = useShoppingCart(clientUUID);

  const disabledCombo = useMemo<boolean>(
    () => !!items.length || !!combo,
    [combo, items]
  );

  const searchInputProps = useEntitySearch({
    addItem,
    client_uuid: clientUUID ?? undefined,
  });

  const total = getShoppingCartTotalPrice(
    items,
    activeDiscount ?? null,
    availableItems
  );
  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [defaultClient, setIsDefaultClient] = useState<string | undefined>('');

  useEffect(() => {
    reset({ ...DEFAULT_FORM_VALUES, clientUUID, discount, promoCode });
    searchInputProps.ref.current?.focus();
  }, [tab, searchInputProps.ref.current]);

  useEffect(() => {
    if (!promoCode?.id) {
      setAvailableItems([]);
    }
  }, [promoCode]);

  useEffect(() => {
    setValue('method', null, { shouldValidate: true });
  }, [items]);

  useEffect(() => {
    clearShoppingCart();
  }, []);

  const { createPayment } = usePayment([GOODS, COMBOS]);

  useEffect(() => {
    setValue('cash_sum', '', { shouldValidate: true });
    setValue('card_sum', '', { shouldValidate: true });
  }, [method, total]);

  const cartItems = useMemo<ISendingPaymentItem[]>(
    () => getCartItems(items, combo, activeDiscount),
    [items, combo, activeDiscount]
  );

  return (
    <form
      className={s['shop-form']}
      onSubmit={handleSubmit(
        ({ clientUUID, discount, promoCode, method, card_sum }) => {
          if (!method || items.length < 1) {
            return;
          }

          if (method === 'DEPOSIT') {
            notification.info({
              t,
              type: 'warning',
              title: t('shop.deposit.notTariff'),
              dismiss: 3,
            });

            return;
          }

          return createPayment({
            method,
            clientUUID,
            cardSum: card_sum || 0,
            promoCodeId: promoCode?.id,
            discountId: discount?.id,
            availableItems: availableItems,
            items: cartItems,
            shopItems: items,
            discountValue: activeDiscount ?? null,
          })
            .then(() => {
              clearShoppingCart();
              setCombo(null);
              reset(DEFAULT_FORM_VALUES);
            })
            .catch(console.error);
        }
      )}
    >
      {isDepositOpen && (
        <DepositModal
          defaultClientUUID={defaultClient}
          onClose={setIsDepositOpen}
        />
      )}
      <div className={s['left']}>
        <Controller
          control={control}
          name="clientUUID"
          render={({ field: { value, onChange } }) => (
            <ClientSearch
              chosenClientUUID={value}
              setChosenClientUUID={onChange}
              disabled={
                !permissions?.has('client_browse') &&
                !permissions?.has('client_info')
              }
              setIsDepositOpen={setIsDepositOpen}
              setIsDefaultClient={setIsDefaultClient}
              setIsRoleClient={setIsRoleClient}
              withReplenishment
            />
          )}
        />
        <Controller
          render={({ field: { value, onChange } }) => (
            <DiscountsField
              value={value}
              onChange={onChange}
              disabled={
                Boolean(promoCode) ||
                tab === 'COMBO' ||
                !items.some((item) => item.use_global_discounts)
              }
            />
          )}
          name="discount"
          control={control}
        />

        {permissions?.has('promo_code_check') && (
          <Controller
            render={({ field: { value, onChange } }) => (
              <PromoCodeField
                value={value}
                onChange={onChange}
                disabled={
                  Boolean(discount) ||
                  tab === 'COMBO' ||
                  !items.some((item) => item.use_global_discounts)
                }
                clientUUID={clientUUID}
                items={items}
                setAvailableItems={setAvailableItems}
              />
            )}
            name="promoCode"
            control={control}
          />
        )}
      </div>
      <div className={s['center']}>
        {promoCode && (
          <div className={s.promo_disabled}>
            <H2>{t('shopForm.promo.disabled')}</H2>
            <span className={s.text}>{t('shopForm.promo.disabled.text')}</span>
          </div>
        )}
        <div className={s.controls}>
          <Tabs
            value={tab}
            options={TABS}
            onChange={setTab}
            className={s.tabs}
          />
          <Input
            variant="secondary"
            {...searchInputProps}
            placeholder={t('search.placeholder')}
            leftContent={<SearchIcon color="primary" />}
            errorWrapperClassName={s.search}
            dataTestId="shop-search"
          />
        </div>
        {tab === 'SERVICE' && (
          <ShopServicesTable
            onItemSelect={addItem}
            clientUUID={clientUUID}
            search={searchInputProps.value}
            combo={combo}
            promoCode={Boolean(promoCode?.id)}
          />
        )}
        {tab === 'GOOD' && (
          <ShopGoodsTable
            onItemSelect={addItem}
            clientUUID={clientUUID}
            search={searchInputProps.value}
            combo={combo}
            promoCode={Boolean(promoCode?.id)}
          />
        )}
        {tab === 'COMBO' && (
          <ShopCombosTable
            onItemSelect={addItem}
            clientUUID={clientUUID}
            disabledCombo={disabledCombo}
            setCombo={setCombo}
            search={searchInputProps.value}
            promoCode={Boolean(promoCode?.id)}
          />
        )}
      </div>
      <div className={s['shopping-cart']}>
        <div className={s['shopping-info']}>
          <ShoppingCart
            combo={combo}
            setCombo={setCombo}
            items={items}
            discount={activeDiscount}
            setItemAmount={setItemAmount}
            removeItem={removeItem}
            emptyPlaceholder={t('shoppingCart.empty')}
            availableItems={availableItems}
          />
          <div className={s.info}>
            <div className={cn(s.discount, s.description)}>
              <span>{t('discount')}</span>
              <span>
                {activeDiscount &&
                tab !== 'COMBO' &&
                items.some((item) => item.use_global_discounts)
                  ? `${activeDiscount}%`
                  : '—'}
              </span>
            </div>
            <Separator />
          </div>
        </div>
        <div className={s.bottom}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Tabs
                options={PAYMENT_METHODS}
                value={value}
                onChange={onChange}
              />
            )}
            name="method"
            control={control}
            rules={{ required: true }}
          />
          {method === EPaymentMethod.CASH && (
            <CalculatorCash totalCash={total} />
          )}
          {method === EPaymentMethod.COMPOSITE && (
            <div className={s.composite}>
              <Controller
                render={({ field, fieldState: { error, isTouched } }) => (
                  <NumberInput
                    {...field}
                    onChange={(value) => {
                      if (value === '') {
                        field.onChange('');
                        setValue('card_sum', '');
                        return;
                      }

                      if (value.toString().split('.')[1]?.length > 2) {
                        return field.onChange(value.toFixed(2));
                      }

                      const formattedValue = parseFloat(value.toFixed(2));

                      field.onChange(formattedValue);

                      if (!total) {
                        return setValue('card_sum', '');
                      }

                      setValue(
                        'card_sum',
                        Number((total - formattedValue).toFixed(2))
                      );
                    }}
                    error={isTouched ? error?.message : undefined}
                    title={t('money.cash')}
                    rightContent={currency.sign}
                    variant="primary"
                    bordered
                  />
                )}
                control={control}
                name="cash_sum"
                rules={{
                  validate: (value) => {
                    if (method !== EPaymentMethod.COMPOSITE) return;

                    if (value === '') return t('errors.required');

                    if (value <= 0) {
                      return t('errors.min', 0.01, currency.sign);
                    }

                    if (value >= (total ?? 0)) {
                      return t('errors.split.minSum');
                    }
                  },
                }}
              />
              <Controller
                render={({ field, fieldState: { error, isTouched } }) => (
                  <NumberInput
                    {...field}
                    onChange={(value) => {
                      if (value === '') {
                        field.onChange('');
                        setValue('card_sum', '');
                        return;
                      }

                      if (value.toString().split('.')[1]?.length > 2) {
                        return field.onChange(value.toFixed(2));
                      }

                      const formattedValue = parseFloat(value.toFixed(2));

                      field.onChange(formattedValue);

                      if (!total) {
                        return setValue('cash_sum', '');
                      }

                      setValue(
                        'cash_sum',
                        Number((total - formattedValue).toFixed(2))
                      );
                    }}
                    error={isTouched ? error?.message : undefined}
                    title={t('money.card')}
                    rightContent={currency.sign}
                    variant="primary"
                    bordered
                  />
                )}
                control={control}
                name="card_sum"
                rules={{
                  required: t('errors.required'),
                  validate: (value) => {
                    if (method !== EPaymentMethod.COMPOSITE) return;

                    if (value === '') return t('errors.required');

                    if (value <= 0) {
                      return t('errors.min', 0.01, currency.sign);
                    }

                    if (value >= (total ?? 0)) {
                      return t('errors.split.minSum');
                    }
                  },
                }}
              />
            </div>
          )}
          <Button
            disabled={!isValid || isRoleClient === 'active_operator'}
            isLoading={isSubmitting}
            fullWidth
            type="submit"
          >
            <div className={s['submit-button']}>
              <span>{t('shoppingCart.submit')}</span>
              <span>
                {total.toLocaleString()} {currency.sign}
              </span>
            </div>
          </Button>
        </div>
      </div>
    </form>
  );
};
